import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';

import { adminApi } from '../../services/adminApi';

const PAGE_SIZE = 25;

// Coarse failure categories the scraper records (orchestrator.js). Kept as a
// curated list for the filter; the table still renders any value the API
// returns, so an unlisted future category is never hidden.
const ERROR_TYPE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'fetch', label: 'Fetch' },
  { value: 'extract', label: 'Extract' },
  { value: 'parse', label: 'Parse' },
  { value: 'timeout', label: 'Timeout' },
];

const ERROR_TYPE_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  fetch: 'error',
  timeout: 'warning',
  extract: 'warning',
  parse: 'warning',
};

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * Admin scrape-error log page (issue #213, §9.5).
 *
 * Surfaces persisted scraper crawl failures from `GET /api/admin/scrape-errors`
 * so an operator can see WHY a vendor crawl fails — the fetch / parse / extract
 * error detail and history the Redis fail-counters and the retailer degraded
 * flag never captured. Paginated, newest-first, with a retailer-id and
 * error-type filter. Auth is enforced upstream by AdminLayout (redirects
 * non-admins); the server also re-checks admin role.
 *
 * Privacy: every value shown is operational metadata about retailers and the
 * scrape pipeline — no end-user PII.
 */
export function ScrapeErrorsPage() {
  const [retailerIdDraft, setRetailerIdDraft] = useState('');
  const [retailerId, setRetailerId] = useState('');
  const [errorType, setErrorType] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'scrape-errors', { retailerId, errorType, page }],
    queryFn: () =>
      adminApi.listScrapeErrors({
        retailerId: retailerId || undefined,
        errorType: errorType || undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setRetailerId(retailerIdDraft.trim());
    setPage(1);
  }

  function onErrorTypeChange(event: SelectChangeEvent<string>) {
    setErrorType(event.target.value);
    setPage(1);
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Scrape errors
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Persisted crawl failures — why a vendor crawl failed, newest first. Operational metadata
        only.
      </Typography>

      <Paper
        component="form"
        onSubmit={onSubmit}
        variant="outlined"
        elevation={0}
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="scrape-error-type-label">Error type</InputLabel>
          <Select
            labelId="scrape-error-type-label"
            id="scrape-error-type"
            value={errorType}
            label="Error type"
            onChange={onErrorTypeChange}
          >
            {ERROR_TYPE_FILTERS.map((t) => (
              <MenuItem key={t.value || 'all'} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id="scrape-error-retailer-id"
          label="Retailer ID"
          size="small"
          value={retailerIdDraft}
          onChange={(e) => setRetailerIdDraft(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button type="submit" variant="contained">
          Filter
        </Button>
      </Paper>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading scrape errors" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load scrape errors: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Scrape errors">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Retailer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">HTTP</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.errors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No scrape errors match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.errors.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell>{formatTimestamp(e.createdAt)}</TableCell>
                      <TableCell>{e.retailerName ?? e.retailerSlug ?? '—'}</TableCell>
                      <TableCell>
                        {e.errorType ? (
                          <Chip
                            label={e.errorType}
                            color={ERROR_TYPE_COLOR[e.errorType] ?? 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{e.httpStatus ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 420, wordBreak: 'break-word' }}>
                        {e.message ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Scrape errors pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default ScrapeErrorsPage;
